import { supabase } from "@/integrations/supabase/client";

/**
 * Resolves an ID that may be either a courses.id or a challenges.id
 * to the canonical course-type challenge.id.
 *
 * Resolution order:
 * 1. Direct match in challenges (fast path when caller already has challenge_id)
 * 2. Look up courses row, then match via woocommerce_product_id -> purchase_reference
 * 3. Fallback: match via title (fragile, kept for legacy compatibility)
 */
export async function resolveChallengeId(idOrChallengeId: string): Promise<string | null> {
  // Direct match in challenges (any type — courses and regular challenges both use CourseView now)
  const { data: asChallenge } = await supabase
    .from("challenges")
    .select("id")
    .eq("id", idOrChallengeId)
    .maybeSingle();
  if (asChallenge?.id) return asChallenge.id;

  // Fallback: legacy courses table lookup
  const { data: course } = await supabase
    .from("courses")
    .select("id, title, woocommerce_product_id")
    .eq("id", idOrChallengeId)
    .maybeSingle();
  if (!course) return null;

  if (course.woocommerce_product_id) {
    const { data: byRef } = await supabase
      .from("challenges")
      .select("id")
      .eq("challenge_type", "course")
      .eq("purchase_reference", course.woocommerce_product_id)
      .maybeSingle();
    if (byRef?.id) return byRef.id;
  }

  const { data: byTitle } = await supabase
    .from("challenges")
    .select("id")
    .eq("challenge_type", "course")
    .eq("title", course.title)
    .maybeSingle();
  return byTitle?.id ?? null;
}

/**
 * Resolves a courses.id specifically (not a challenge_id) to its
 * course-type challenge.id. Used by coursesService where the caller
 * is always working with the legacy courses table.
 */
export async function resolveCourseToChallengeId(courseId: string): Promise<string | null> {
  const { data: course, error } = await supabase
    .from("courses")
    .select("id, title, woocommerce_product_id")
    .eq("id", courseId)
    .maybeSingle();
  if (error || !course) return null;

  if (course.woocommerce_product_id) {
    const { data: byRef } = await supabase
      .from("challenges")
      .select("id")
      .eq("challenge_type", "course")
      .eq("purchase_reference", course.woocommerce_product_id)
      .maybeSingle();
    if (byRef?.id) return byRef.id;
  }

  const { data: byTitle } = await supabase
    .from("challenges")
    .select("id")
    .eq("challenge_type", "course")
    .eq("title", course.title)
    .maybeSingle();
  return byTitle?.id ?? null;
}

/**
 * Reverse map: given a set of challenge_ids, return the corresponding
 * courses.id values. Used by the library to compare access (keyed by
 * challenge_id) against the courses table (keyed by courses.id).
 */
export async function mapChallengeIdsToCourseIds(challengeIds: string[]): Promise<Record<string, string>> {
  if (challengeIds.length === 0) return {};

  const { data: challenges } = await supabase
    .from("challenges")
    .select("id, title, purchase_reference")
    .eq("challenge_type", "course")
    .in("id", challengeIds);

  if (!challenges?.length) return {};

  const titles = challenges.map(c => c.title);
  const refs = challenges.map(c => c.purchase_reference).filter(Boolean) as string[];

  const orClauses = [
    `title.in.(${titles.map(t => `"${t.replace(/"/g, '\\"')}"`).join(",")})`,
    refs.length ? `woocommerce_product_id.in.(${refs.join(",")})` : "",
  ].filter(Boolean).join(",");

  const { data: courses } = await supabase
    .from("courses")
    .select("id, title, woocommerce_product_id")
    .or(orClauses);

  if (!courses?.length) return {};

  const map: Record<string, string> = {};
  for (const challenge of challenges) {
    let courseMatch = null;
    if (challenge.purchase_reference) {
      courseMatch = courses.find(c => c.woocommerce_product_id === challenge.purchase_reference);
    }
    if (!courseMatch) {
      courseMatch = courses.find(c => c.title === challenge.title);
    }
    if (courseMatch) {
      map[challenge.id] = courseMatch.id;
    }
  }
  return map;
}
